/**
 * The lock family's own reading — one fold for the widget body, its tile, its
 * chips and the write they drive (card t_e483b8cc).
 *
 * A lock's state arrives under more than one spelling, and both the read and
 * the write have to settle on the key the device actually carries:
 *
 *  - `locked` — the hub's own spelling (the dev hub's Covered Entrance Door
 *    Lock carries exactly this one row): `true` / `1` / `'locked'` is locked.
 *    The chrome's pill read it all along (`DeviceControlModal`'s `stateText`
 *    case 'lock') while the widget body's fold knew only the two older shapes
 *    below, so one sheet read `Locked` in the head and `Unlocked` in the body.
 *  - the legacy LockButton shape: an `unlock` property row — `false` is locked.
 *  - the legacy LockControl shape: the power keys `power` / `on_off` / `state`
 *    — `true` / `1` / `'on'` is locked.
 *
 * `lockWriteKey` answers the write's half of the same question: a device
 * carrying `locked` is written through `locked` (its own spelling), everything
 * else keeps the `unlock` write the older bodies made — `useDeviceControl`'s
 * `setProperty` resolves that name onto the row the device has (`unlock`, or a
 * power / state key).
 */

/** One device-value row as the lock's fold reads it — the app's `valueKey` plus
 *  the raw `property_key` spelling the Z-Wave / lock payloads still carry. */
export type LockValueRow = {
  valueKey?: unknown;
  property_key?: unknown;
  currentValue?: unknown;
};

/** The row's own key, whichever of the two spellings it arrived under. */
function rowKey(row: LockValueRow): string {
  const key = typeof row.valueKey === 'string' && row.valueKey !== '' ? row.valueKey : row.property_key;
  return typeof key === 'string' ? key : '';
}

/** `true` / `1` / `'on'` — a boolean datapoint's on-reading. */
function on(value: unknown): boolean {
  return value === true || value === 1 || value === 'on';
}

/** The reading for `key`: the live bag first (the widget's socket subscription,
 *  initialized from the device's rows), else the rows' own value — the same
 *  order the chrome's `readValue` and the widget's power fold read. */
function read(rows: readonly LockValueRow[] | null | undefined, live: Record<string, unknown>, key: string): unknown {
  if (key in live) return live[key];
  return rows?.find((row) => rowKey(row) === key)?.currentValue;
}

/**
 * Is the lock locked? The fold mirrors the chrome's own, in this order:
 *
 *  1. `locked` — `true` / `1` / `'locked'` is locked (the chrome's pill);
 *  2. an `unlock` property row — `false` is locked (LockButton);
 *  3. the power keys — `true` / `1` / `'on'` is locked (LockControl).
 *
 * A device carrying none of them reads unlocked — never a guess.
 */
export function isLockLocked(rows: readonly LockValueRow[] | null | undefined, live: Record<string, unknown>): boolean {
  const locked = read(rows, live, 'locked');
  if (locked !== undefined) return locked === true || locked === 1 || locked === 'locked';

  const unlock = rows?.find((row) => row.property_key === 'unlock');
  if (unlock !== undefined) return unlock.currentValue === false;

  for (const key of ['power', 'on_off', 'state']) {
    if (key in live) return on(live[key]);
  }
  const row = rows?.find((candidate) => ['power', 'on_off', 'state'].includes(rowKey(candidate)));
  return row !== undefined && on(row.currentValue);
}

/**
 * The value key a locked/unlocked write goes to: the `locked` row's own
 * spelling when the device carries one (`true` = locked), else `unlock` — the
 * write the older bodies made, which `useDeviceControl` resolves onto the
 * device's `unlock` property or a power / state key.
 */
export function lockWriteKey(rows: readonly LockValueRow[] | null | undefined): 'locked' | 'unlock' {
  const carriesLocked = rows?.some((row) => rowKey(row) === 'locked') ?? false;
  return carriesLocked ? 'locked' : 'unlock';
}
